/**
 * Netzwerk-Service — Care-Web (Pflegeeinrichtungs-Perspektive)
 *
 * Suche nach Praxen/Einrichtungen, Partnerschaften anfragen und verwalten.
 * RPCs: search_network_practices, search_network_facilities,
 *       request_organization_partnership, respond_to_partnership,
 *       get_organization_partners, get_pending_partnership_requests
 */
import { requireSupabase, ensureValidSession } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type {
    OrganizationPartnership,
    NetworkPracticeSearchResult,
    NetworkFacilitySearchResult,
    OrganizationType,
    NetworkDashboardStats,
    PartnerInteropStats,
    NetworkActivityItem,
    NetworkRegistryMeta,
    NetworkFacilitiesMeta,
} from "../_vendored-shared/web/organization-partnership";
import {
    mapPartnershipRow,
    mapPartnerRpcRow,
    mapPendingRequestRpcRow,
    mapPracticeSearchRow,
    mapFacilitySearchRow,
    mapDashboardStatsRow,
    mapPartnerInteropRow,
    mapActivityFeedRow,
    mapRegistryMetaRow,
    mapFacilitiesMetaRow,
} from "../_vendored-shared/web/organization-partnership-service";

// ── Helpers ─────────────────────────────────────────────────

function getSingleRpcRecord(data: unknown): Record<string, unknown> | null {
    if (!data) return null;
    if (Array.isArray(data)) {
        const [first] = data;
        return (first as Record<string, unknown> | undefined) ?? null;
    }
    return data as Record<string, unknown>;
}

function getRpcArray(data: unknown): Record<string, unknown>[] {
    if (!data) return [];
    if (Array.isArray(data)) return data as Record<string, unknown>[];
    return [];
}

// ── Search ──────────────────────────────────────────────────

export async function searchNetworkPractices(
    query: string,
    postalCode?: string,
    specialty?: string,
): Promise<NetworkPracticeSearchResult[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("search_network_practices", {
            p_query: query,
            p_postal_code: postalCode ?? null,
            p_specialty: specialty ?? null,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapPracticeSearchRow);
    } catch (e) {
        logger.error("network.searchPractices", e);
        return [];
    }
}

export async function searchNetworkFacilities(
    query: string,
    postalCode?: string,
    facilityType?: string,
): Promise<NetworkFacilitySearchResult[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("search_network_facilities", {
            p_query: query,
            p_postal_code: postalCode ?? null,
            p_facility_type: facilityType ?? null,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapFacilitySearchRow);
    } catch (e) {
        logger.error("network.searchFacilities", e);
        return [];
    }
}

export async function getNetworkRegistryMeta(): Promise<NetworkRegistryMeta> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_network_registry_meta");
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        if (!row) return { totalPractices: 0, specialties: [] };
        return mapRegistryMetaRow(row);
    } catch (e) {
        logger.error("network.getRegistryMeta", e);
        return { totalPractices: 0, specialties: [] };
    }
}

export async function getNetworkFacilitiesMeta(): Promise<NetworkFacilitiesMeta> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_network_facilities_meta");
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        if (!row) return { totalFacilities: 0, facilityTypes: [] };
        return mapFacilitiesMetaRow(row);
    } catch (e) {
        logger.error("network.getFacilitiesMeta", e);
        return { totalFacilities: 0, facilityTypes: [] };
    }
}

// ── Partnerships ────────────────────────────────────────────

export async function requestPartnership(
    requesterType: "care",
    requesterId: string,
    targetType: OrganizationType,
    targetId: string,
    categories: string[],
    message?: string,
): Promise<OrganizationPartnership | null> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();
        const { data, error } = await sb.rpc("request_organization_partnership", {
            p_requester_type: requesterType,
            p_requester_id: requesterId,
            p_target_type: targetType,
            p_target_id: targetId,
            p_categories: categories,
            p_message: message ?? null,
        });
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        return row ? mapPartnershipRow(row) : null;
    } catch (e) {
        logger.error("network.requestPartnership", e);
        return null;
    }
}

export async function respondToPartnership(
    partnershipId: string,
    accept: boolean,
    rejectionReason?: string,
): Promise<OrganizationPartnership | null> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();
        const { data, error } = await sb.rpc("respond_to_partnership", {
            p_partnership_id: partnershipId,
            p_accept: accept,
            p_rejection_reason: rejectionReason ?? null,
        });
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        return row ? mapPartnershipRow(row) : null;
    } catch (e) {
        logger.error("network.respondToPartnership", e);
        return null;
    }
}

export async function getPartners(
    facilityId: string,
): Promise<OrganizationPartnership[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_organization_partners", {
            p_org_type: "care",
            p_org_id: facilityId,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapPartnerRpcRow);
    } catch (e) {
        logger.error("network.getPartners", e);
        return [];
    }
}

export async function getPendingRequests(
    facilityId: string,
): Promise<OrganizationPartnership[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_pending_partnership_requests", {
            p_org_type: "care",
            p_org_id: facilityId,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapPendingRequestRpcRow);
    } catch (e) {
        logger.error("network.getPendingRequests", e);
        return [];
    }
}

// ── Dashboard & Activity ────────────────────────────────────

export async function getDashboardStats(
    orgType: OrganizationType,
    orgId: string,
): Promise<NetworkDashboardStats> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_network_dashboard_stats", {
            p_org_type: orgType,
            p_org_id: orgId,
        });
        if (error) throw error;
        return mapDashboardStatsRow(getSingleRpcRecord(data) ?? {});
    } catch (e) {
        logger.error("network.getDashboardStats", e);
        return { activePartners: 0, pendingRequests: 0, activeCareLinks: 0, openCareRequests: 0, activeReferrals: 0 };
    }
}

export async function getPartnerInteropStats(
    orgType: OrganizationType,
    orgId: string,
): Promise<PartnerInteropStats[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_partner_interop_stats", {
            p_org_type: orgType,
            p_org_id: orgId,
        });
        if (error) throw error;
        return getRpcArray(data).map((row) => mapPartnerInteropRow(row));
    } catch (e) {
        logger.error("network.getPartnerInteropStats", e);
        return [];
    }
}

/** Returns open request counts per practice grouped by category. */
export async function getOpenRequestBreakdown(
    facilityId: string,
): Promise<Map<string, Record<string, number>>> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb
            .from("care_practice_requests")
            .select("practice_id, category")
            .eq("facility_id", facilityId)
            .not("status", "eq", "ERLEDIGT");
        if (error) throw error;
        const result = new Map<string, Record<string, number>>();
        for (const row of (data ?? []) as Array<{ practice_id: string; category: string }>) {
            if (!row.practice_id) continue;
            const bucket = result.get(row.practice_id) ?? {};
            bucket[row.category] = (bucket[row.category] ?? 0) + 1;
            result.set(row.practice_id, bucket);
        }
        return result;
    } catch (e) {
        logger.error("network.getOpenRequestBreakdown", e);
        return new Map();
    }
}

export async function getActivityFeed(
    orgType: OrganizationType,
    orgId: string,
    limit = 20,
): Promise<NetworkActivityItem[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_network_activity_feed", {
            p_org_type: orgType,
            p_org_id: orgId,
            p_limit: limit,
        });
        if (error) throw error;
        return getRpcArray(data).map((row) => mapActivityFeedRow(row));
    } catch (e) {
        logger.error("network.getActivityFeed", e);
        return [];
    }
}
